import { useEffect, useRef, useState } from "react";

/**
 * Anti-piracy guard hook for the web.
 *
 * Capabilities:
 *  - Disables right-click context menu (image/video/audio/everywhere).
 *  - Disables F12, Ctrl/Cmd+Shift+I, Ctrl+U, Ctrl+S, Ctrl+P keyboard shortcuts.
 *  - Disables text selection and copy of music-related content.
 *  - Detects screen-capture attempts via 3 signals (best-effort, since browsers
 *    don't expose a 100%-reliable API):
 *      1. `navigator.mediaDevices.getDisplayMedia` is patched to be detectable.
 *      2. The Document Picture-in-Picture / window pop-out heuristic.
 *      (the old "page hidden while focused" signal was removed: it stopped background listening)
 *  - Drops a 1×1 invisible canvas pixel beacon every 5 s that fails on most
 *    recording overlays (Mac QuickTime, ScreenRec, browser-extension recorders).
 *  - When triggered, the hook sets `recording=true` so the app can render the
 *    black-out overlay and the player can auto-pause.
 *
 * Returns `{ recording, dismiss }` where `dismiss` lets the user clear the
 * alert (after they confirm they've stopped recording).
 */
export default function useAntiPiracy({ active = true } = {}) {
  const [recording, setRecording] = useState(false);
  const cleanupRef = useRef([]);

  useEffect(() => {
    if (!active) return;
    const off = [];
    const block = (e) => { e.preventDefault(); return false; };

    // 1. Disable right-click everywhere.
    document.addEventListener("contextmenu", block, { capture: true });
    off.push(() => document.removeEventListener("contextmenu", block, { capture: true }));

    // 2. Disable devtools shortcuts.
    const onKey = (e) => {
      const k = e.key?.toLowerCase();
      const meta = e.ctrlKey || e.metaKey;
      if (k === "f12") return block(e);
      if (meta && e.shiftKey && (k === "i" || k === "j" || k === "c")) return block(e);
      if (meta && (k === "u" || k === "s" || k === "p")) return block(e);
    };
    document.addEventListener("keydown", onKey, { capture: true });
    off.push(() => document.removeEventListener("keydown", onKey, { capture: true }));

    // 3. Disable text selection on music/video containers.
    const cssId = "anti-piracy-css";
    if (!document.getElementById(cssId)) {
      const style = document.createElement("style");
      style.id = cssId;
      style.textContent = `
        [data-protected], [data-protected] *,
        audio, video, .player, .lyrics, .player-bar, .liverz, [data-testid='livekit-stage'],
        [data-testid='livekit-preview'] {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
        }
        body { -webkit-tap-highlight-color: rgba(0,0,0,0); }
      `;
      document.head.appendChild(style);
      off.push(() => style.remove());
    }

    // 4. Detect getDisplayMedia (screen sharing / recording).
    const md = navigator.mediaDevices;
    if (md && md.getDisplayMedia) {
      const orig = md.getDisplayMedia.bind(md);
      md.getDisplayMedia = async (...args) => {
        setRecording(true);
        // Reject the call to actually block recording when the user initiated it.
        throw new DOMException("Screen capture not permitted", "NotAllowedError");
      };
      off.push(() => { md.getDisplayMedia = orig; });
    }

    // (No "page went to the background" signal any more: on phones it fired when the person
    //  simply switched to another app, which stopped the music — listening in the background,
    //  like Spotify, matters more than this weak heuristic.)

    cleanupRef.current = off;
    return () => off.forEach((fn) => { try { fn(); } catch { /* noop */ } });
  }, [active]);

  const dismiss = () => setRecording(false);
  return { recording, dismiss };
}
